import React from "react";
import { fmtNumber, getLatestSnapshot, addMonths, padZip5 } from "../utils/helpers";
import ChartRow from "./ChartRow";

const DataTable = {

  _renderDelta(delta, lowerIsBetter = false) {
    if (delta == null || delta === 0) {
      return <span style={{ marginLeft: 6, color: "#666" }}>-</span>;
    }
    const isUp = delta > 0;
    const improved = lowerIsBetter ? !isUp : isUp;
    const arrow = isUp ? "▲" : "▼";
    const color = improved ? "#118a00" : "#c22727";
    const percentage = Math.abs(delta * 100);
    return (
      <span style={{ color, marginLeft: 6 }}>
        {arrow} {percentage.toFixed(1)}%
      </span>
    );
  },

  // Best Potential table
  renderBestPotentialTable: ({ bestPotential, latestByMetro, setSelectedMetro, setSelectedName, setView, allDates, latestDate, areaType, snapshotView = 'bestPotential-snapshot', afterHeading = null }) => {
    if (!bestPotential?.length) {
      return (
        <div style={{
          padding: "20px",
          textAlign: "center",
          fontSize: "16px",
          color: "#666",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px"
        }}>
          <div style={{
            width: "20px",
            height: "20px",
            border: "3px solid #e0e0e0",
            borderTop: "3px solid #666",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }} />
          Thinking...
        </div>
      );
    }

    const rows100 = bestPotential.slice(0, 100); // show top 100

    const displayAreaLabel = areaType === 'Neighborhood' ? 'Neighbourhood' : (areaType || 'Metro');

    return (
      <>
        {afterHeading}
        <div className="table-responsive">
        <table className="table" style={{ marginTop: "36px" }}>
          <thead>
            <tr>
              <th>#</th>
              <th>{displayAreaLabel}</th>
              <th>
                TP date
                <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                  <span className="hdr-tip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                      <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                      <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                    </svg>
                  </span>
                  <span className="hdr-tip-tooltip" role="tooltip">Turning Point — Dramatic shift in the market from 24 consecutive months of price declines to price growth</span>
                </span>
              </th>
              <th className="hide-sm" style={{ textAlign: "right" }}>
                Price YoY at TP
                <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                  <span className="hdr-tip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                      <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                      <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                    </svg>
                  </span>
                  <span className="hdr-tip-tooltip" role="tooltip">Year-over-year price change (%) at the turning point date</span>
                </span>
              </th>
              <th className="hide-sm" style={{ textAlign: "right" }}>
                Price YoY
                <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                  <span className="hdr-tip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                      <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                      <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                    </svg>
                  </span>
                  <span className="hdr-tip-tooltip" role="tooltip">Year-over-year price change (%) vs the same month last year</span>
                </span>
              </th>
              <th style={{ textAlign: "right" }}>
                Latest Price
                <span className="hdr-tip" aria-hidden="true">
                  <span className="hdr-tip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                      <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                      <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                    </svg>
                  </span>
                  <span className="hdr-tip-tooltip" role="tooltip">Last month price and the change (%) from the previous month</span>
                </span>
              </th>
              <th style={{ textAlign: "right" }}>
                Latest Rent
                <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                  <span className="hdr-tip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                      <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                      <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                    </svg>
                  </span>
                  <span className="hdr-tip-tooltip" role="tooltip">Last month rent and the change (%) from the previous month</span>
                </span>
              </th>
              <th className="hide-sm" style={{ textAlign: "right" }}>
                Latest Inventory
                <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                  <span className="hdr-tip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                      <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                      <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                    </svg>
                  </span>
                  <span className="hdr-tip-tooltip" role="tooltip">Number of homes listed for sale (current month)</span>
                </span>
              </th>
              <th className="hide-sm" style={{ textAlign: "right" }}>
                Latest DOM
                <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                  <span className="hdr-tip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                      <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                      <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                      <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                    </svg>
                  </span>
                  <span className="hdr-tip-tooltip" role="tooltip">Average number of days on market for a property to sell</span>
                </span>
              </th>
              <th>Data</th>
              <th>Map</th>
            </tr>
          </thead>
          <tbody>
            {rows100.map((r, i) => {
              const latest = getLatestSnapshot(r.metro, latestByMetro) || {};

              // Resolve the correct series key by probing row fields (works for all levels)
              const seriesKey =
                r.state ?? r.State ??
                r.metro ?? r.Metro ??
                r.county ?? r.County ?? r.CountyName ??
                r.city ?? r.City ??
                r.zipcode ?? r.Zipcode ??
                r.neighborhood ?? r.Neighborhood ??
                r.Name;

              // Use precomputed YoY values only
              const _yoyA = Number(r.PriceYoY);
              const _yoyB = Number(r.price12); // legacy alias seen in CSVs
              const dPriceYoY = (!Number.isNaN(_yoyA)) ? _yoyA
                : (!Number.isNaN(_yoyB)) ? _yoyB
                : null;

              // Price YoY at TP comes from the Best Potential score (fraction → %)
              const tpYoYPct = (typeof r.score === 'number' && isFinite(r.score)) ? (r.score * 100) : null;

              return (
                <tr key={`${seriesKey || r.metro || i}-${i}`}>
                  <td>{i + 1}</td>
                  <td>{seriesKey || r.metro}</td>
                  <td>{(() => {
                    const tpDate = r.tp_date || r.tpDate || '';
                    if (!tpDate) return '';
                    // Format to YYYY-MM: handle both YYYY-MM-DD and YYYY-MM formats
                    const dateStr = String(tpDate).trim();
                    return dateStr.length >= 7 ? dateStr.substring(0, 7) : dateStr;
                  })()}</td>
                  <td className="hide-sm" style={{ textAlign: "right" }}>{tpYoYPct != null ? `${tpYoYPct.toFixed(1)}%` : '-'}</td>
                  <td className="hide-sm" style={{ textAlign: "right" }}>{dPriceYoY != null ? `${Number(dPriceYoY).toFixed(1)}%` : '-'}</td>
                  <td style={{ textAlign: "right" }}>{latest.Price != null ? fmtNumber(latest.Price, 0) : '-'}</td>
                  <td style={{ textAlign: "right" }}>{latest.Rent != null ? fmtNumber(latest.Rent, 0) : '-'}</td>
                  <td className="hide-sm" style={{ textAlign: "right" }}>{latest.Inventory != null ? fmtNumber(latest.Inventory, 0) : '-'}</td>
                  <td className="hide-sm" style={{ textAlign: "right" }}>{latest.Days != null ? fmtNumber(latest.Days, 0) : '-'}</td>
                  <td>
                    <button
                      className="btn"
                      title="Open chart"
                      onClick={() => {
                        if (seriesKey) {
                          setSelectedMetro(seriesKey);
                          setSelectedName(seriesKey);
                          setView(snapshotView);
                        }
                      }}
                    >
                      <span role="img" aria-label="Open chart">📈</span>
                    </button>
                  </td>
                  <td>
                    <a
                      className="btn"
                      title="Open in Google Maps"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(seriesKey || r.metro))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      🗺️
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>
    );
  },

  // Metro data table
  renderMetroData: ({ currentMetro, selectedMetro, showChart, allDates, latestDate, forecastByName, areaType, linksByName, setAreaType, setSelectedMetro, setSelectedName, setView }) => {
    console.log("[DATATABLE] renderMetroData called with:", {
      currentMetro,
      hasCurrentMetro: !!currentMetro,
      currentMetroType: typeof currentMetro,
      selectedMetro,
      areaType
    });
    
    if (!currentMetro) {
      console.log("[DATATABLE] renderMetroData returning null - currentMetro is falsy");
      return null;
    }
    
    // Helper to open a deeper level and show its data
    const lastNavRef = { key: '', ts: 0 };
    const openDataDeep = async (level, name) => {
      try {
        if (setAreaType) setAreaType(level);
        const target = String(name || "").trim();
        const navKey = level + '|' + target;
        const nowTs = Date.now();
        if (lastNavRef.key === navKey && (nowTs - lastNavRef.ts) < 300) return;
        lastNavRef.key = navKey; lastNavRef.ts = nowTs;
        if (areaType === level && selectedMetro === target) { if (setView) setView('metro'); return; }
        if (setSelectedMetro) setSelectedMetro(target);
        if (setSelectedName) setSelectedName(target);
        const deadline = Date.now() + 2500;
        while (Date.now() < deadline) {
          const has = Array.isArray(allDates) && allDates.some(r => String(r?.Metro || "").trim() === target || (level === 'Zipcode' && String(r?.Metro || "").replace(/\D+/g, '').padStart(5,'0').slice(0,5) === target));
          if (has) break;
          // eslint-disable-next-line no-await-in-loop
          await new Promise(r => setTimeout(r, 100));
        }
        // Removed automatic chart loading - charts should only load when user clicks chart buttons
        // await showChart('Price', target);
        if (setView) setView('metro');
      } catch {}
    };

    const renderExploreCell = () => {
      if (!linksByName || !selectedMetro) return null;
      const link = (
        areaType === 'State' ? linksByName?.State?.[selectedMetro]
        : areaType === 'Metro' ? linksByName?.Metro?.[selectedMetro]
        : areaType === 'City' ? linksByName?.City?.[selectedMetro]
        : areaType === 'County' ? linksByName?.County?.[selectedMetro]
        : areaType === 'Zipcode' ? linksByName?.Zipcode?.[selectedMetro]
        : null
      );
      if (!link) return null;

      const openBtn = (lvl, nm, label) => (
        <button
          className="btn"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDataDeep(lvl, String(nm)); }}
        >
          {label}
        </button>
      );

      if (areaType === 'State' && link.BestMetro) {
        return (
          <details>
            <summary>Explore…</summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
              {openBtn('Metro', link.BestMetro, `Metro: ${link.BestMetro}`)}
              {link.BestCounty ? openBtn('County', link.BestCounty, `County: ${link.BestCounty}`) : null}
              {link.BestCity ? openBtn('City', link.BestCity, `City: ${link.BestCity}`) : null}
              {link.BestZipcode ? openBtn('Zipcode', String(link.BestZipcode).replace(/\.0$/, ''), `Zipcode: ${String(link.BestZipcode).replace(/\.0$/, '')}`) : null}
            </div>
          </details>
        );
      }

      if (areaType === 'Metro' && (link.BestCounty || link.BestCity || link.BestZipcode || link.BestNeighborhood)) {
        return (
          <details>
            <summary>Explore…</summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
              {link.BestCounty ? openBtn('County', link.BestCounty, `County: ${link.BestCounty}`) : null}
              {link.BestCity ? openBtn('City', link.BestCity, `City: ${link.BestCity}`) : null}
              {link.BestZipcode ? openBtn('Zipcode', String(link.BestZipcode).replace(/\.0$/, ''), `Zipcode: ${String(link.BestZipcode).replace(/\.0$/, '')}`) : null}
              {link.BestNeighborhood ? openBtn('Neighborhood', link.BestNeighborhood, `Neighborhood: ${link.BestNeighborhood}`) : null}
            </div>
          </details>
        );
      }

      if (areaType === 'City' && (link.BestZipcode || link.BestNeighborhood)) {
        return (
          <details>
            <summary>Explore…</summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
              {link.BestZipcode ? openBtn('Zipcode', String(link.BestZipcode).replace(/\.0$/, ''), `Zipcode: ${String(link.BestZipcode).replace(/\.0$/, '')}`) : null}
              {link.BestNeighborhood ? openBtn('Neighborhood', link.BestNeighborhood, `Neighborhood: ${link.BestNeighborhood}`) : null}
            </div>
          </details>
        );
      }

      if (areaType === 'County' && (link.BestCity || link.BestZipcode || link.BestNeighborhood)) {
        return (
          <details>
            <summary>Explore…</summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
              {link.BestCity ? openBtn('City', link.BestCity, `City: ${link.BestCity}`) : null}
              {link.BestZipcode ? openBtn('Zipcode', String(link.BestZipcode).replace(/\.0$/, ''), `Zipcode: ${String(link.BestZipcode).replace(/\.0$/, '')}`) : null}
              {link.BestNeighborhood ? openBtn('Neighborhood', link.BestNeighborhood, `Neighborhood: ${link.BestNeighborhood}`) : null}
            </div>
          </details>
        );
      }

      if (areaType === 'Zipcode' && link.BestNeighborhood) {
        return (
          <details>
            <summary>Explore…</summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
              {openBtn('Neighborhood', link.BestNeighborhood, `Neighborhood: ${link.BestNeighborhood}`)}
            </div>
          </details>
        );
      }

      return null;
    };

    // Dynamic label for the first column based on current level
    const displayAreaLabel = areaType === 'Neighborhood' ? 'Neighbourhood' : (areaType || 'Metro');

    return (
      <>
      <div className="table-responsive">
      <table className="table" style={{ marginTop: "20px" }}>
        <thead>
          <tr>
            <th>{displayAreaLabel}</th>
            <th>
              Price
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Last month price and the change (%) from the previous month</span>
              </span>
            </th>
            <th className="hide-sm">
              Price YoY
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Year-over-year price change (%) vs the same month last year</span>
              </span>
            </th>
            <th className="hide-sm">
              DOM
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Average number of days on market for a property to sell</span>
              </span>
            </th>
            <th className="hide-sm">
              Inventory
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Number of homes listed for sale (current month)</span>
              </span>
            </th>
            <th>
              Rent
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Last month rent and the change (%) from the previous month</span>
              </span>
            </th>
            <th>
              Fcst 1m
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 1 month</span>
              </span>
            </th>
            <th>
              Fcst 3m
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 3 months</span>
              </span>
            </th>
            <th>
              Fcst 12m
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 12 months</span>
              </span>
            </th>
            <th>
              Explore
              <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                <span className="hdr-tip-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                    <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                    <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                    <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                  </svg>
                </span>
                <span className="hdr-tip-tooltip" role="tooltip">Explore shows the best performing child areas related to your area selection.</span>
              </span>
            </th>
            <th>Chart</th>
            <th>Map</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{
              areaType === 'Zipcode' ? currentMetro.Zipcode : 
              areaType === 'City' ? currentMetro.City :
              areaType === 'County' ? (currentMetro.CountyName || currentMetro.County) :
              areaType === 'Neighborhood' ? currentMetro.Neighborhood :
              areaType === 'State' ? currentMetro.State :
              currentMetro.Metro
            }</td>
            <td>
              {currentMetro.Price != null ? fmtNumber(currentMetro.Price, 0) : "-"}
              {DataTable._renderDelta(currentMetro.Price_delta, false)}
            </td>
            <td className="hide-sm">
              {DataTable._renderDelta(currentMetro.Price_YoY_delta, false)}
            </td>
            <td className="hide-sm">
              {currentMetro.Days != null ? fmtNumber(currentMetro.Days, 0) : "-"}
              {DataTable._renderDelta(currentMetro.DOM_delta, true)}
            </td>
            <td className="hide-sm">
              {currentMetro.Inventory != null ? fmtNumber(currentMetro.Inventory, 0) : "-"}
              {DataTable._renderDelta(currentMetro.Inventory_delta, true)}
            </td>
            <td>
              {currentMetro.Rent != null ? fmtNumber(currentMetro.Rent, 0) : "-"}
              {DataTable._renderDelta(currentMetro.Rent_delta, false)}
            </td>
            {(() => {
              const key = areaType === 'Zipcode' ? padZip5(currentMetro.Zipcode) : 
                          areaType === 'County' ? String(currentMetro.CountyName || currentMetro.County || '').trim() :
                          areaType === 'City' ? String(currentMetro.City || '').trim() :
                          areaType === 'Neighborhood' ? String(currentMetro.Neighborhood || '').trim() :
                          areaType === 'State' ? String(currentMetro.State || '').trim() :
                          String(currentMetro.Metro || '').trim();
              const fmtPct = (v) => (v != null && Number.isFinite(v) ? `${Number(v).toFixed(1)}%` : '--');
              
              // Use the same pattern as DOM and Inventory - check currentMetro directly
              const f1m = currentMetro?.f1m;
              const f3m = currentMetro?.f3m;
              const f12m = currentMetro?.f12m;
              
              return (
                <>
                  <td>{f1m != null ? fmtPct(f1m) : '-'}</td>
                  <td>{f3m != null ? fmtPct(f3m) : '-'}</td>
                  <td>{f12m != null ? fmtPct(f12m) : '-'}</td>
                </>
              );
            })()}
            <td>{renderExploreCell()}</td>
            <td>
              <select
                className="btn"
                onChange={(e) => { 
                  const v = e.target.value; 
                  if (v) {
                    const target = areaType === 'Zipcode' ? padZip5(currentMetro.Zipcode) : 
                                  areaType === 'County' ? String(currentMetro.CountyName || currentMetro.County || '').trim() :
                                  areaType === 'City' ? String(currentMetro.City || '').trim() :
                                  areaType === 'Neighborhood' ? String(currentMetro.Neighborhood || '').trim() :
                                  areaType === 'State' ? String(currentMetro.State || '').trim() :
                                  String(currentMetro.Metro || '').trim();
                    showChart(v, target);
                  }
                  e.target.value=''; 
                }}
                disabled={!selectedMetro}
                defaultValue=""
              >
                <option value="" disabled>Chart…</option>
                <option value="Price">Price</option>
                <option value="Days">DOM</option>
                <option value="Inventory">Inventory</option>
                <option value="Rent">Rent</option>
              </select>
            </td>
            <td>
              <a
                className="btn"
                title="Open in Google Maps"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(currentMetro.Metro))}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                🗺️
              </a>
            </td>
          </tr>
        </tbody>
      </table>
        </div>
      </>
    );
  },

  // Performance/Potential ranking table
  renderRankTable: ({ type, ranks, scores: scoreData, latestByMetro, latestDate, showChart, allDates, areaType, linksByName, setAreaType, setSelectedMetro, setSelectedName, setView, forecastByName, afterHeading = null }) => {
    // Show spinner while Best Performing data is loading or not yet populated
    const hasRankMap = !!(ranks && ranks.performance && Object.keys(ranks.performance || {}).length);
    const hasScoreMap = !!(scoreData && scoreData.performance && Object.keys(scoreData.performance || {}).length);

    if (!hasRankMap || !hasScoreMap) {
      return (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            fontSize: "16px",
            color: "#666",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <div
            style={{
              width: "20px",
              height: "20px",
              border: "3px solid #e0e0e0",
              borderTop: "3px solid #666",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          Thinking...
        </div>
      );
    }

    const rankMap   = ranks.performance;
    const scoreMap  = scoreData.performance;
    const titleText = "Best Performing";

    // Build ranking from existing rank map, then (for City level) require at least
    // two latest snapshot metrics to be present (e.g., Price/Rent/Inventory/Days)
    // Filter out entries without a finite numeric rank so the list always starts at Rank 1
    const sortedByRank = Object.entries(rankMap || {})
      .filter(([, r]) => Number.isFinite(Number(r)) && Number(r) >= 1)
      .sort((a, b) => Number(a[1]) - Number(b[1]));
    const minLatestMetrics = 2;
    const hasMinLatest = (latest) => {
      if (!latest) return false;
      const vals = [latest.Price, latest.Rent, latest.Inventory, latest.Days];
      let present = 0;
      for (const v of vals) {
        if (v != null && Number.isFinite(Number(v))) present += 1;
      }
      return present >= minLatestMetrics;
    };
    let ranked = sortedByRank.map(([metro, rank]) => ({
      metro,
      rank,
      latest: getLatestSnapshot(metro, latestByMetro),
      score: scoreMap?.[metro] ?? null,
    }));
    if (areaType === 'City') {
      ranked = ranked.filter((r) => hasMinLatest(r.latest));
      ranked = ranked.map((r, idx) => ({ ...r, rank: idx + 1 }));
    }
    let top = ranked.slice(0, 20);

    // Helper: switch level, wait for data, then open chart (data page)
    const openDataDeep = async (level, name) => {
      try {
        setAreaType(level);
        setSelectedMetro("");
        setSelectedName("");
        const target = String(name || "").trim();
        const deadline = Date.now() + 2500; // up to 2.5s
        // wait until allDates includes the target on the new level
        while (Date.now() < deadline) {
          const has = Array.isArray(allDates) && allDates.some(r => String(r?.Metro || "").trim() === target || (level === 'Zipcode' && String(r?.Metro || "").replace(/\D+/g, '').padStart(5,'0').slice(0,5) === target));
          if (has) break;
          await new Promise(r => setTimeout(r, 100));
        }
        // Removed automatic chart loading - charts should only load when user clicks chart buttons
        // await showChart('Price', target);
      } catch {}
    };

    return (
      <>
        <h3 style={{ marginTop: "30px" }}>
          {(() => {
            const levelLabel = areaType || "Metro";
            return `Top 20 - ${titleText} Markets - ${levelLabel} Level`;
          })()}
          {latestDate && (
            <span style={{ marginLeft: "20px", fontWeight: 400, fontSize: "14px" }}>
              as of {latestDate}
            </span>
          )}
        </h3>
        {afterHeading}

        <div className="table-responsive">
          <table className="table" style={{ marginTop: "36px" }}>
            <thead>
              <tr>
                <th>Rank</th>
                <th>{areaType || "Metro"}</th>
                <th>
                  Price
                  <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                    <span className="hdr-tip-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                        <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                        <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                      </svg>
                    </span>
                    <span className="hdr-tip-tooltip" role="tooltip">Last month price and the change (%) from the previous month</span>
                  </span>
                </th>
                <th className="hide-sm">
                  Price YoY
                  <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                    <span className="hdr-tip-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                        <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                        <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                      </svg>
                    </span>
                    <span className="hdr-tip-tooltip" role="tooltip">Year-over-year price change (%) vs the same month last year</span>
                  </span>
                </th>
                <th className="hide-sm">
                  Rent
                  <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                    <span className="hdr-tip-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                        <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                        <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                      </svg>
                    </span>
                    <span className="hdr-tip-tooltip" role="tooltip">Last month rent and the change (%) from the previous month</span>
                  </span>
                </th>
                <th className="hide-sm">
                  Inventory
                  <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                    <span className="hdr-tip-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                        <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                        <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                      </svg>
                    </span>
                    <span className="hdr-tip-tooltip" role="tooltip">Number of homes listed for sale (current month)</span>
                  </span>
                </th>
                <th className="hide-sm">
                  DOM
                  <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                    <span className="hdr-tip-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                        <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                        <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                      </svg>
                    </span>
                    <span className="hdr-tip-tooltip" role="tooltip">Average number of days on market for a property to sell</span>
                  </span>
                </th>
                {/* Forecasts */}
                <th>
                  Fcst 1m
                  <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                    <span className="hdr-tip-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                        <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                        <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                      </svg>
                    </span>
                    <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 1 month</span>
                  </span>
                </th>
                <th>
                  Fcst 3m
                  <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                    <span className="hdr-tip-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                        <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                        <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                      </svg>
                    </span>
                    <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 3 months</span>
                  </span>
                </th>
                <th>
                  Fcst 12m
                  <span className="hdr-tip" aria-hidden="true" style={{ marginLeft: 6 }}>
                    <span className="hdr-tip-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="6" y="6" width="12" height="10" rx="2" ry="2" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="4" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <rect x="18" y="9" width="2" height="6" fill="none" stroke="#333" strokeWidth="2" />
                        <circle cx="12" cy="4" r="1.4" fill="none" stroke="#333" strokeWidth="2" />
                        <line x1="12" y1="5.4" x2="12" y2="6" stroke="#333" strokeWidth="2" />
                        <text x="12" y="13.5" textAnchor="middle" fontSize="9" fill="#333" fontFamily="Segoe UI, Arial, sans-serif">?</text>
                      </svg>
                    </span>
                    <span className="hdr-tip-tooltip" role="tooltip">Price change forecast (%) over the next 12 months</span>
                  </span>
                </th>
                <th className="hide-sm">Score (z)</th>
                <th>Data</th>
                <th>Map</th>
              </tr>
            </thead>
            <tbody>
              {top.map((r) => (
                <ChartRow
                  key={r.metro}
                  rank={r.rank}
                  metro={r.metro}
                  latest={r.latest}
                  score={r.score}
                  showChart={showChart}
                  allDates={allDates}
                  latestDate={latestDate}
                  precomputedPriceYoY={(scoreData?.performancePrice12 && scoreData.performancePrice12?.[r.metro]) ?? null}
                  showMapInline={false}
                  addMapCell={true}
                  areaType={areaType}
                  setSelectedMetro={setSelectedMetro}
                  setView={setView}
                  type={type}
                />
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }
};

export default DataTable;
